import React, { useMemo } from 'react'
import { Box, Tooltip } from '@mui/material'
import { ClockIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, TYPOGRAPHY } from '../constants/interaxColors'
import { SprintActivity } from '../../../pure-js/types/Sprint'
import { ActivityType } from '../../../pure-js/enums/ActivityType'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import { SprintActivityProgressStatus } from './SprintActivityProgressStatus'
import { LocalTimeBadge } from '../shared/components/LocalTimeBadge'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

export interface SprintActivityItemForGamerDashboardProps {
  activity: SprintActivity
  isCompleted: boolean
  isInProgress: boolean
  onClick?: () => void
  className?: string
}

// Helper function to get activity icon based on type
const getActivityIcon = (_type: ActivityType) => {
  // Use ClockIcon as default for all activity types
  return ClockIcon
}

// Helper function to calculate duration in minutes
const calculateDuration = (startDate: string, endDate: string): string => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  const diff = end.diff(start, 'minute')

  if (diff < 60) {
    return `${diff} min`
  } else {
    const hours = Math.floor(diff / 60)
    const minutes = diff % 60
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
  }
}

export const SprintActivityItemForGamerDashboard: React.FC<SprintActivityItemForGamerDashboardProps> = ({
  activity,
  isCompleted,
  isInProgress,
  onClick,
  className = ''
}) => {
  const { t } = useTranslation()

  // Get translated activity type
  const activityTypeLabel = useMemo(() => {
    if (!activity.type) return t('gamer.activityTypes.default', 'Activity')
    return t(`gamer.activityTypes.${activity.type}`, activity.type)
  }, [activity.type, t])

  // Calculate duration
  const durationLabel = useMemo(() => {
    return calculateDuration(activity.startDate, activity.endDate)
  }, [activity.startDate, activity.endDate])

  // Format date as "12 JAN 15:30"
  const formattedDate = useMemo(() => {
    const date = dayjs(activity.startDate)
    const day = date.format('D') // Day without leading zero
    const month = date.format('MMM').toUpperCase() // Short month in uppercase
    const time = date.format('HH:mm') // 24-hour time format
    return `${day} ${month} ${time}`
  }, [activity.startDate])

  // Get icon component
  const ActivityIcon = getActivityIcon(activity.type)

  return (
    <Box>
      <Box
        className={className}
        onClick={onClick}
        sx={{
          position: 'relative',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: 2,
          paddingRight: '30px',
          gap: 2,
          width: '100%',
          minWidth: { xs: 0, sm: '280px', md: '280px' },
          height: '82px',
          background:
            'radial-gradient(50.19% 50.19% at 50% 50%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), rgba(46, 79, 95, 0.5)',
          backgroundBlendMode: 'soft-light, normal',
          border: `1px solid ${INTERAX_COLORS.white10}`,
          boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white10}`,
          borderRadius: '10px',
          transition: 'all 0.2s ease-in-out',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick
            ? {
                transform: 'translateY(-2px)',
                boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white20}, 0px 4px 8px rgba(0, 0, 0, 0.2)`,
                borderColor: INTERAX_COLORS.sun
              }
            : {
                transform: 'translateY(-2px)',
                boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white20}, 0px 4px 8px rgba(0, 0, 0, 0.2)`
              }
        }}
      >
        {/* Icon Container */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 48,
            height: 48,
            backgroundColor: INTERAX_COLORS.sun20,
            borderRadius: '10px',
            flexShrink: 0
          }}
        >
          <ActivityIcon
            style={{
              width: 24,
              height: 24,
              color: INTERAX_COLORS.sun
            }}
          />
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            flex: 1,
            minWidth: 0
          }}
        >
          {/* Title Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              width: '100%'
            }}
          >
            <Box
              component="span"
              sx={{
                ...TYPOGRAPHY.headline5,
                color: INTERAX_COLORS.white,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {activityTypeLabel}
            </Box>
          </Box>

          {/* Duration Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              width: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <ClockIcon
                style={{
                  width: 16,
                  height: 16,
                  color: INTERAX_COLORS.thunderGrey
                }}
              />
              <Box
                component="span"
                sx={{
                  ...TYPOGRAPHY.body3,
                  color: INTERAX_COLORS.thunderGrey
                }}
              >
                {durationLabel}
              </Box>
              {activity.description && (
                <Tooltip title={activity.description} placement="top" arrow>
                  <InformationCircleIcon
                    style={{
                      width: 16,
                      height: 16,
                      color: INTERAX_COLORS.sun,
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Box
              sx={{
                ...TYPOGRAPHY.body3,
                color: INTERAX_COLORS.white,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <ClockIcon
                style={{
                  width: 16,
                  height: 16,
                  color: INTERAX_COLORS.thunderGrey
                }}
              />
              {formattedDate}
              <LocalTimeBadge />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
        <SprintActivityProgressStatus isCompleted={isCompleted} isInProgress={isInProgress} />
      </Box>
    </Box>
  )
}
